import { layoutTokens, typographyTokens, surfaceTokens, ACTIVE_SURFACE_MUTED } from "@/shared/theme";
import { Box, Typography } from "@mui/material";
import type { PatternEvidenceVolume, PatternMovement } from "../patterns.api";

const percent = (value: number) => {
  const scaled = Math.round(value * 1000) / 10;
  return Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(1);
};
const TenGameRail = ({ label, events, inverse = false }: { label: string; events: number; inverse?: boolean }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        color: inverse ? ACTIVE_SURFACE_MUTED : "text.secondary",
        fontSize: "caption.fontSize",
        fontWeight: "fontWeightBold",
      }}
    >
      {label} {events} of 10 games
    </Typography>
    <Box aria-hidden sx={{ mt: 0.5, display: "grid", gridTemplateColumns: "repeat(10,minmax(0,1fr))", gap: 0.25 }}>
      {Array.from({ length: 10 }, (_, index) => (
        <Box
          component="span"
          key={index}
          sx={{
            height: layoutTokens.evidenceRailHeight,
            border: layoutTokens.dividerBorder,
            borderColor: inverse
              ? index < events
                ? "background.default"
                : surfaceTokens.inverseDivider
              : index < events
                ? "primary.main"
                : "divider",
            bgcolor: inverse && index < events ? "background.default" : index < events ? "primary.main" : "transparent",
          }}
        />
      ))}
    </Box>
  </Box>
);

export const PatternMovementRail = ({
  movement,
  inverse = false,
}: {
  movement: PatternMovement;
  inverse?: boolean;
}) => {
  if (movement.status === "insufficient_data")
    return (
      <Typography
        data-evidence-rail="movement"
        sx={{
          mt: 0.75,
          color: inverse ? ACTIVE_SURFACE_MUTED : "text.secondary",
          fontSize: "caption.fontSize",
          fontWeight: "fontWeightBold",
        }}
      >
        {movement.neededGames} more games needed for comparison
      </Typography>
    );
  const label =
    movement.classification === "fading"
      ? "Improving ↓"
      : movement.classification === "emerging"
        ? "Needs attention ↑"
        : movement.classification === "persistent"
          ? "Recurring"
          : "No clear direction yet";
  return (
    <Box
      data-evidence-rail="movement"
      role="img"
      aria-label={`${label}. Previous ${movement.prior.events} of 10 games; recent ${movement.recent.events} of 10 games.`}
      sx={{ mt: 0.75 }}
    >
      <Typography
        sx={{
          color: inverse
            ? "background.default"
            : movement.classification === "fading"
              ? "success.main"
              : "primary.main",
          fontSize: "body2.fontSize",
          fontWeight: "fontWeightBold",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{ mt: 0.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 0.75 }}
      >
        <TenGameRail inverse={inverse} label="Previous" events={movement.prior.events} />
        <TenGameRail inverse={inverse} label="Recent" events={movement.recent.events} />
      </Box>
    </Box>
  );
};

export const RecurrenceRail = ({ volume }: { volume: PatternEvidenceVolume }) => {
  const low = volume.interval95[0] ?? volume.recurrenceRate;
  const high = volume.interval95[1] ?? volume.recurrenceRate;
  return (
    <Box
      data-evidence-rail="recurrence"
      role="img"
      aria-label={`Recurrence ${percent(volume.recurrenceRate)} percent. ${volume.occurrences} of ${volume.eligibleDecisions} eligible decisions. 95 percent interval ${percent(low)} to ${percent(high)} percent.`}
      sx={{ mt: 1.25, pt: 1, borderTop: layoutTokens.dividerBorder, borderColor: "divider" }}
    >
      <Typography
        sx={{
          fontSize: "caption.fontSize",
          fontWeight: "fontWeightBold",
          letterSpacing: typographyTokens.tracking.label,
        }}
      >
        RECURRENCE {percent(volume.recurrenceRate)}%
      </Typography>
      <Box
        aria-hidden
        sx={{
          mt: 0.5,
          height: layoutTokens.evidenceRailHeight,
          position: "relative",
          border: layoutTokens.dividerBorder,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: `${volume.recurrenceRate * 100}%`, height: "100%", bgcolor: "primary.main" }} />
        <Box
          sx={{
            position: "absolute",
            insetBlock: -1,
            left: `${low * 100}%`,
            width: `${Math.max(0, high - low) * 100}%`,
            borderInline: `${layoutTokens.structuralBorder}px solid`,
            borderColor: "secondary.main",
          }}
        />
      </Box>
      <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 0.5 }}>
        <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
          {volume.occurrences} / {volume.eligibleDecisions} ELIGIBLE DECISIONS
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
          95% INTERVAL {percent(low)}%–{percent(high)}%
        </Typography>
      </Box>
    </Box>
  );
};

export const DetectedSignalRail = ({
  missed,
  applied,
  interval,
}: {
  missed: number;
  applied: number;
  interval: number[];
}) => {
  const detected = missed + applied;
  const rate = detected ? missed / detected : 0;
  const low = interval[0] ?? rate;
  const high = interval[1] ?? rate;
  return (
    <Box
      data-evidence-rail="detected-signals"
      role="img"
      aria-label={`${missed} missed and ${applied} applied of ${detected} detected signals. Missed rate ${percent(rate)} percent; 95 percent interval ${percent(low)} to ${percent(high)} percent.`}
      sx={{ mt: 1.25, border: layoutTokens.dividerBorder, borderColor: "divider" }}
    >
      <Box aria-hidden sx={{ display: "flex", height: layoutTokens.evidenceRailHeight }}>
        <Box sx={{ width: `${rate * 100}%`, bgcolor: "secondary.main" }} />
        <Box sx={{ flex: 1, bgcolor: "primary.main" }} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
        <Typography sx={{ p: 1, fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
          MISSED SIGNALS {missed}/{detected} DETECTED SIGNALS
        </Typography>
        <Typography
          sx={{
            p: 1,
            borderTop: { xs: layoutTokens.dividerBorder, sm: 0 },
            borderLeft: { sm: layoutTokens.dividerBorder },
            borderColor: "divider",
            fontSize: "caption.fontSize",
            fontWeight: "fontWeightBold",
          }}
        >
          APPLIED SIGNALS {applied}/{detected} DETECTED SIGNALS
        </Typography>
      </Box>
      <Typography
        sx={{ px: 1, pb: 1, color: "text.secondary", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}
      >
        MISSED RATE {percent(rate)}% · 95% INTERVAL {percent(low)}%–{percent(high)}%
      </Typography>
    </Box>
  );
};
